import React, { useState, useEffect } from 'react';
import styles from './AddPartyForm.module.css';

interface AddPartyFormProps {
  onSubmit: (partyData: PartyFormData) => void;
  onCancel: () => void;
  initialData?: Partial<PartyFormData>;
}

export interface PartyFormData {
  name: string;
  mobile: string;
  email: string;
  address: string;
  pan: string;
  status: 'active' | 'inactive';
  note: string;
}

const AddPartyForm: React.FC<AddPartyFormProps> = ({ onSubmit, onCancel, initialData }) => {
  const [formData, setFormData] = useState<PartyFormData>({
    name: '',
    mobile: '',
    email: '',
    address: '',
    pan: '',
    status: 'active',
    note: '',
  });

  // Populate form with initial data when editing
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {/* Name */}
      <div className={styles.formGroup}>
        <label htmlFor="name" className={styles.label}>
          Name <span className={styles.required}>*</span>
        </label>
        <input
          id="name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={styles.input}
          required
        />
      </div>

      {/* Mobile and Email Row */}
      <div className={styles.row}>
        <div className={styles.formGroup}>
          <label htmlFor="mobile" className={styles.label}>Mobile No.</label>
          <input
            id="mobile"
            type="text"
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            className={styles.input}
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="email" className={styles.label}>Email</label>
          <input
            id="email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={styles.input}
          />
        </div>
      </div>

      {/* Address */}
      <div className={styles.formGroup}>
        <label htmlFor="address" className={styles.label}>Address</label>
        <textarea
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          className={styles.textarea}
          rows={3}
        />
      </div>

      {/* PAN and Status Row */}
      <div className={styles.row}>
        <div className={styles.formGroup}>
          <label htmlFor="pan" className={styles.label}>PAN</label>
          <input
            id="pan"
            type="text"
            name="pan"
            value={formData.pan}
            onChange={handleChange}
            className={styles.input}
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="status" className={styles.label}>Status</label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            className={styles.select}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Note */}
      <div className={styles.formGroup}>
        <label htmlFor="note" className={styles.label}>Note</label>
        <textarea
          id="note"
          name="note"
          value={formData.note}
          onChange={handleChange}
          className={styles.textarea}
          rows={3}
        />
      </div>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <button type="button" onClick={onCancel} className={styles.cancelButton}>
          Cancel
        </button>
        <button type="submit" className={styles.createButton}>
          {initialData ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default AddPartyForm;
